/**
 * React-Query-Haken — ein Ort für API-Aufrufe (Paket 10 §2).
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { api, grundVon, hinweisVon, istNetzfehler } from "./api.js";
import { schluessel, ungueltigNach, type MutationArt } from "./abfragen.js";
import type { AufgabenFilter } from "./filter.js";
import { filterZuEingabe } from "./filter.js";
import { meldungMitHinweis } from "./meldung.js";
import type {
  Aufgabenart,
  QualitaetsGateModus,
  QualitaetsGateSchwelle,
} from "@cubic/shared";
import { qualitaetsGateLaeuft } from "./qualitaets-gate-zustand.js";

export interface CursorSeite<T> {
  eintraege: T[];
  naechsterCursor: string | null;
}

/**
 * Einheitlicher React-Query-Haken für die cursorbasierten Fachlisten.
 *
 * Der Cursor bleibt opak und wird ausschließlich an den Dienst zurückgegeben.
 * Auf diese Weise besitzt keine Oberfläche Wissen über Sortierung oder Aufbau
 * des Cursors, und alle geladenen Seiten stehen als flache Liste bereit.
 */
export function useCursorListe<T>({
  queryKey,
  lade,
  enabled = true,
  aktualisierung,
}: {
  queryKey: readonly unknown[];
  lade: (cursor: string | undefined) => Promise<CursorSeite<T>>;
  enabled?: boolean;
  aktualisierung?: {
    alleMs: number;
    solange: (eintraege: T[]) => boolean;
  };
}) {
  const abfrage = useInfiniteQuery({
    queryKey,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => lade(pageParam),
    getNextPageParam: (seite) => seite.naechsterCursor ?? undefined,
    enabled,
    refetchInterval: aktualisierung
      ? (zustand) => {
          const seiten = zustand.state.data?.pages ?? [];
          const eintraege = seiten.flatMap((seite) => seite.eintraege);
          return aktualisierung.solange(eintraege)
            ? aktualisierung.alleMs
            : false;
        }
      : false,
    refetchIntervalInBackground: false,
  });
  return {
    ...abfrage,
    eintraege: abfrage.data?.pages.flatMap((seite) => seite.eintraege) ?? [],
  };
}

async function invalidiere(
  client: QueryClient,
  art: MutationArt,
  kontext: Parameters<typeof ungueltigNach>[1],
) {
  const keys = ungueltigNach(art, kontext);
  await Promise.all(
    keys.map((k) => client.invalidateQueries({ queryKey: [...k] })),
  );
}

export function useIch() {
  return useQuery({
    queryKey: schluessel.ich(),
    queryFn: () => api.ich.query(),
  });
}

/**
 * Eine Person mit Namen.
 *
 * `ich` liefert Kennung, Rollen und Rechte, aber keinen Namen — der steht in
 * `personen`. Die eigene Person darf jede Sicht lesen, fremde brauchen
 * Verwaltungsrecht; für den Fuß der Schiene ist immer die eigene gemeint.
 */
export function usePerson(id: string | undefined) {
  return useQuery({
    queryKey: schluessel.person(id ?? ""),
    queryFn: () => api.personen.lies.query({ id: id! }),
    enabled: Boolean(id),
  });
}

/**
 * Die Zählwerte der Schiene.
 *
 * Eine Abfrage für alle sechs Zahlen. `projektId` bleibt offen, wenn „Alle
 * Projekte" gewählt ist — dann zählen Aufgaben und Klärungen über alle
 * sichtbaren Projekte, und die Projektzahlen kommen als `null` zurück.
 */
export function useNavigationszahlen(projektId?: string) {
  return useQuery({
    queryKey: schluessel.navigationszahlen(projektId),
    queryFn: () =>
      api.navigation.zaehler.query(
        projektId ? { projektId } : {},
      ),
  });
}

export function useProjekte(archivierte = false) {
  return useQuery({
    queryKey: schluessel.projekte(archivierte),
    queryFn: () => api.projekte.liste.query({ archivierte }),
  });
}

export function useProjekt(id: string | undefined) {
  return useQuery({
    queryKey: schluessel.projekt(id ?? ""),
    queryFn: () => api.projekte.lies.query({ id: id! }),
    enabled: Boolean(id),
  });
}

export function useProjektVerzeichnis(id: string | undefined) {
  return useQuery({
    queryKey: schluessel.projektVerzeichnis(id ?? ""),
    queryFn: () => api.projekte.verzeichnis.query({ projektId: id! }),
    enabled: Boolean(id),
  });
}

export function useAufgabenFuerProjekte(
  projektIds: readonly string[],
  filter: AufgabenFilter,
) {
  return useQuery({
    queryKey: schluessel.aufgabenAlle(filter, projektIds),
    queryFn: async () => {
      const { projektIds: ids } = filterZuEingabe(filter, projektIds);
      if (ids.length === 0) return [];
      const listen = await Promise.all(
        ids.map((projektId) =>
          api.aufgaben.liste.query({ projektId }).then((zeilen) =>
            zeilen.map((z) => ({ ...z, projektId })),
          ),
        ),
      );
      return listen.flat();
    },
    enabled: projektIds.length > 0,
  });
}

export function useAufgabe(id: string | undefined) {
  return useQuery({
    queryKey: schluessel.aufgabe(id ?? ""),
    queryFn: () => api.aufgaben.detail.query({ id: id! }),
    select: (detail) => detail.aufgabe,
    enabled: Boolean(id),
  });
}

export function useZustaende(projektId: string | undefined) {
  return useQuery({
    queryKey: schluessel.zustaende(projektId ?? ""),
    queryFn: () => api.zustaende.liste.query({ projektId: projektId! }),
    enabled: Boolean(projektId),
  });
}

export function useZustaendeFuerProjekte(projektIds: readonly string[]) {
  const sortierteIds = [...projektIds].sort();
  return useQuery({
    queryKey: ["cubic", "zustaende", "projekte", sortierteIds.join(",")],
    queryFn: async () => {
      const listen = await Promise.all(
        sortierteIds.map((projektId) =>
          api.zustaende.liste.query({ projektId }).then((zustaende) =>
            zustaende.map((zustand) => ({ ...zustand, projektId })),
          ),
        ),
      );
      return listen.flat();
    },
    enabled: sortierteIds.length > 0,
  });
}

export function useMeilensteine(projektId: string | undefined) {
  return useQuery({
    queryKey: schluessel.meilensteine(projektId ?? ""),
    queryFn: () => api.meilensteine.liste.query({ projektId: projektId! }),
    enabled: Boolean(projektId),
  });
}

export function useMeilensteinFortschritt(id: string | undefined) {
  return useQuery({
    queryKey: schluessel.meilensteinFortschritt(id ?? ""),
    queryFn: () => api.meilensteine.fortschritt.query({ id: id! }),
    enabled: Boolean(id),
  });
}

/**
 * Eine Projekteinstellung aus der Registry.
 *
 * Der Server liefert bei fehlender Zeile den Standard aus der Registry zurück
 * — der Haken kennt deshalb keinen „noch nichts gesetzt"-Fall, nur „lädt".
 */
export function useEinstellungImProjekt(
  einstellungsSchluessel: string,
  projektId: string | undefined,
) {
  return useQuery({
    queryKey: schluessel.einstellung(
      einstellungsSchluessel,
      "projekt",
      projektId ?? "",
    ),
    queryFn: () =>
      api.einstellungen.lies.query({
        schluessel: einstellungsSchluessel,
        bereich: "projekt",
        bereichId: projektId!,
      }),
    enabled: Boolean(projektId),
  });
}

export function useEinstellungImProjektSetzen(
  einstellungsSchluessel: string,
  projektId: string,
) {
  const client = useQueryClient();
  const abfrage = schluessel.einstellung(
    einstellungsSchluessel,
    "projekt",
    projektId,
  );
  return useMutation({
    mutationFn: (wert: string) =>
      api.einstellungen.setzen.mutate({
        schluessel: einstellungsSchluessel,
        wert,
        bereich: "projekt",
        bereichId: projektId,
      }),
    onSuccess: async (daten) => {
      /*
       * Der Server kanonisiert den Wert (`normalisiereEinstellungswert`);
       * gezeigt wird deshalb, was zurückkommt, nicht was getippt wurde.
       */
      client.setQueryData(abfrage, daten.wert);
      await client.invalidateQueries({ queryKey: abfrage });
    },
  });
}

export function useQualitaetsGateRegel(projektId: string | undefined) {
  return useQuery({
    queryKey: schluessel.qualitaetsGateRegel(projektId ?? ""),
    queryFn: () =>
      api.qualitaetsGate.regel.query({ projektId: projektId! }),
    enabled: Boolean(projektId),
  });
}

export function useAutoQaAutomationen(projektId: string | undefined) {
  return useQuery({
    queryKey: schluessel.autoQaAutomationen(projektId ?? ""),
    queryFn: () => api.autoQa.automationen.query({ projektId: projektId! }),
    enabled: Boolean(projektId),
  });
}

export function useQualitaetsGateStand(mrId: string | undefined) {
  return useQuery({
    queryKey: schluessel.qualitaetsGateStand(mrId ?? ""),
    queryFn: () => api.qualitaetsGate.stand.query({ mrId: mrId! }),
    enabled: Boolean(mrId),
    refetchInterval: (abfrage) => {
      if (abfrage.state.fetchFailureCount >= 4) return false;
      return qualitaetsGateLaeuft(
        abfrage.state.data?.ergebnis.gruende ?? [],
      )
        ? 5_000
        : false;
    },
    refetchIntervalInBackground: false,
  });
}

export function useQualitaetsGatePruefen(
  projektId: string,
  mrId: string,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.qualitaetsGate.pruefen.mutate({ mrId }),
    onSuccess: async (stand) => {
      client.setQueryData(schluessel.qualitaetsGateStand(mrId), stand);
      await client.invalidateQueries({
        queryKey: schluessel.gitlabMrs(projektId),
      });
    },
  });
}

export function useMergeRequestMergen(projektId: string, mrId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (eingabe: {
      erwarteteKopfSha: string;
      wiederholungsschluessel: string;
    }) => api.gitlab.mergen.mutate({ mrId, ...eingabe }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({
          queryKey: schluessel.gitlabMrs(projektId),
        }),
        client.invalidateQueries({
          queryKey: schluessel.qualitaetsGateStand(mrId),
        }),
      ]);
    },
  });
}

export function useQualitaetsGateUebersteuern(
  projektId: string,
  mrId: string,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (eingabe: { pruefungId: string; begruendung: string }) =>
      api.qualitaetsGate.uebersteuern.mutate({ mrId, ...eingabe }),
    onSuccess: async (stand) => {
      client.setQueryData(schluessel.qualitaetsGateStand(mrId), stand);
      await client.invalidateQueries({
        queryKey: schluessel.gitlabMrs(projektId),
      });
    },
  });
}

export function useQualitaetsGateRegelAendern(projektId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (eingabe: {
      modus: QualitaetsGateModus;
      pipelineErforderlich: boolean;
      autoQaErforderlich: boolean;
      schwereGrenze: QualitaetsGateSchwelle;
      automationIds: string[];
      erwarteteVersion: number;
    }) =>
      api.qualitaetsGate.regelAendern.mutate({ projektId, ...eingabe }),
    onSuccess: async (regel) => {
      client.setQueryData(schluessel.qualitaetsGateRegel(projektId), regel);
      await Promise.all([
        client.invalidateQueries({
          queryKey: schluessel.gitlabMrs(projektId),
        }),
        client.invalidateQueries({
          queryKey: schluessel.qualitaetsGateStaende(),
        }),
      ]);
    },
  });
}

export function useArbeitspakete(
  projektId: string | undefined,
  meilensteinId?: string,
) {
  return useQuery({
    queryKey: schluessel.arbeitspakete(projektId ?? "", meilensteinId),
    queryFn: () =>
      api.arbeitspakete.liste.query({
        projektId: projektId!,
        ...(meilensteinId ? { meilensteinId } : {}),
      }),
    enabled: Boolean(projektId),
  });
}

export function useArbeitspaket(id: string | undefined) {
  return useQuery({
    queryKey: schluessel.arbeitspaket(id ?? ""),
    queryFn: () => api.arbeitspakete.lies.query({ id: id! }),
    enabled: Boolean(id),
  });
}

export function useArbeitspaketFortschritt(id: string | undefined) {
  return useQuery({
    queryKey: schluessel.arbeitspaketFortschritt(id ?? ""),
    queryFn: () => api.arbeitspakete.fortschritt.query({ id: id! }),
    enabled: Boolean(id),
  });
}

export function useAufgabeAnlegen(filter?: AufgabenFilter) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (eingabe: {
      projektId: string;
      titel: string;
      inhalt?: string;
      art?: Aufgabenart;
      prioritaet?: number;
      faelligAm?: Date | null;
      schaetzungMinuten?: number | null;
      arbeitspaketId?: string | null;
      meilensteinId?: string | null;
      zustandId?: string;
    }) => api.aufgaben.anlegen.mutate(eingabe),
    onSuccess: async (_d, eingabe) => {
      await invalidiere(client, "aufgabe_anlegen", {
        projektId: eingabe.projektId,
        filter,
      });
    },
  });
}

export function useAufgabeAendern(filter?: AufgabenFilter) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (eingabe: {
      id: string;
      titel?: string;
      inhalt?: string;
      art?: Aufgabenart;
      prioritaet?: number;
      faelligAm?: Date | null;
      schaetzungMinuten?: number | null;
      arbeitspaketId?: string | null;
      meilensteinId?: string | null;
      erwarteteVersion: number;
      projektId: string;
      meilensteinIdAlt?: string | null;
    }) => {
      const { projektId: _p, meilensteinIdAlt: _m, ...rest } = eingabe;
      return api.aufgaben.aendern.mutate(rest);
    },
    onSuccess: async (_d, eingabe) => {
      await invalidiere(client, "aufgabe_aendern", {
        projektId: eingabe.projektId,
        aufgabeId: eingabe.id,
        meilensteinId: eingabe.meilensteinId ?? eingabe.meilensteinIdAlt,
        filter,
      });
    },
  });
}

export function useAufgabeStatus(filter?: AufgabenFilter) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (eingabe: {
      id: string;
      zustandId: string;
      erwarteteVersion: number;
      projektId: string;
      meilensteinId?: string | null;
    }) =>
      api.aufgaben.status.mutate({
        id: eingabe.id,
        zustandId: eingabe.zustandId,
        erwarteteVersion: eingabe.erwarteteVersion,
      }),
    onSuccess: async (_d, eingabe) => {
      await invalidiere(client, "aufgabe_status", {
        projektId: eingabe.projektId,
        aufgabeId: eingabe.id,
        meilensteinId: eingabe.meilensteinId,
        filter,
      });
    },
  });
}

export function useAufgabeArchivieren(filter?: AufgabenFilter) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (eingabe: {
      id: string;
      projektId: string;
      meilensteinId?: string | null;
    }) => api.aufgaben.archivieren.mutate({ id: eingabe.id }),
    onSuccess: async (_d, eingabe) => {
      await invalidiere(client, "aufgabe_archivieren", {
        projektId: eingabe.projektId,
        aufgabeId: eingabe.id,
        meilensteinId: eingabe.meilensteinId,
        filter,
      });
    },
  });
}

export function useProjektAnlegen() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (eingabe: {
      kuerzel: string;
      name: string;
      beschreibung?: string;
    }) =>
      api.projekte.anlegen.mutate({
        ...eingabe,
        wiederholungsschluessel: `ui-${eingabe.kuerzel}-${Date.now()}`,
      }),
    onSuccess: async (p) => {
      await invalidiere(client, "projekt_anlegen", { projektId: p.id });
    },
  });
}

export function useMeilensteinAnlegen() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (eingabe: {
      projektId: string;
      name: string;
      faelligAm: Date;
      beschreibung?: string;
    }) => api.meilensteine.anlegen.mutate(eingabe),
    onSuccess: async (_d, eingabe) => {
      await invalidiere(client, "meilenstein_anlegen", {
        projektId: eingabe.projektId,
      });
    },
  });
}

export function fehlerMeldung(fehler: unknown) {
  return meldungMitHinweis(grundVon(fehler), hinweisVon(fehler), {
    netzfehler: istNetzfehler(fehler),
  });
}
